import { useCallback, useEffect, useState } from "react";
import { RefreshCw, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n";
import {
  BUILD_ID,
  BUILD_TIME,
  SHORT_BUILD_ID,
  fetchRemoteVersion,
  type RemoteVersion,
  type VersionStatus,
} from "@/lib/version";

const LS_KEY = "lovable-version-badge";
const RECHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Floating badge that surfaces which JS bundle the user has loaded and
 * whether it matches the latest deployed bundle (see /version.json).
 *
 * Visibility rules:
 *   - Always shown when the loaded bundle is out of date (so the user
 *     knows a reload is required — most common after a deploy).
 *   - Otherwise opt-in via `?version=1`, the `lovable-version-badge=1`
 *     localStorage flag, or `window.__LOVABLE_BUILD__.check()` in
 *     DevTools.
 *
 * Re-checks: on mount, when the tab becomes visible, every 5 minutes,
 * and on demand via the refresh button.
 */
export function VersionBadge() {
  const { t } = useI18n();
  const [status, setStatus] = useState<VersionStatus>("checking");
  const [remote, setRemote] = useState<RemoteVersion | null>(null);
  const [checkedAt, setCheckedAt] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const runCheck = useCallback(async () => {
    setStatus((prev) => (prev === "outdated" ? prev : "checking"));
    const r = await fetchRemoteVersion();
    setCheckedAt(Date.now());
    if (!r) {
      setStatus("unknown");
      return;
    }
    setRemote(r);
    setStatus(r.buildId === BUILD_ID ? "current" : "outdated");
  }, []);

  useEffect(() => {
    void runCheck();
    const onVisible = () => {
      if (document.visibilityState === "visible") void runCheck();
    };
    document.addEventListener("visibilitychange", onVisible);
    const interval = window.setInterval(() => {
      void runCheck();
    }, RECHECK_INTERVAL_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(interval);
    };
  }, [runCheck]);

  const forced =
    typeof window !== "undefined" &&
    (new URLSearchParams(window.location.search).get("version") === "1" ||
      window.localStorage.getItem(LS_KEY) === "1");

  // Out-of-date is always visible; otherwise opt-in only.
  if (status !== "outdated" && !forced) return null;
  if (dismissed && status !== "outdated") return null;

  const tone =
    status === "outdated"
      ? "border-destructive/40 bg-destructive/10 text-destructive-foreground"
      : status === "current"
        ? "border-emerald-500/30 bg-emerald-500/10 text-foreground"
        : "border-border bg-background text-foreground";

  const Icon =
    status === "outdated"
      ? AlertTriangle
      : status === "current"
        ? CheckCircle2
        : RefreshCw;

  const label =
    status === "checking"
      ? t("version.checking")
      : status === "current"
        ? t("version.current")
        : status === "outdated"
          ? t("version.outdated")
          : t("version.unknown");

  const handleReload = () => {
    // Strong cache bust — replace so the new entry doesn't sit behind history.
    const url = new URL(window.location.href);
    url.searchParams.set("v", Date.now().toString(36));
    window.location.replace(url.toString());
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "fixed bottom-3 left-3 z-[200] flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs shadow-md backdrop-blur",
        tone,
      )}
    >
      <Icon
        className={cn(
          "h-3.5 w-3.5 shrink-0",
          status === "checking" && "animate-spin",
        )}
        aria-hidden
      />
      <span className="font-medium">{label}</span>
      <span className="font-mono text-[10px] opacity-70" title={BUILD_TIME}>
        {SHORT_BUILD_ID}
        {remote && remote.buildId !== BUILD_ID
          ? ` → ${remote.buildId.slice(0, 8)}`
          : ""}
      </span>
      {status === "outdated" ? (
        <button
          type="button"
          onClick={handleReload}
          className="ml-1 rounded-full bg-destructive px-2 py-0.5 text-[11px] font-semibold text-destructive-foreground hover:opacity-90"
        >
          {t("version.reload")}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => void runCheck()}
          aria-label={t("version.recheck")}
          className="ml-1 rounded-full p-1 hover:bg-foreground/10"
        >
          <RefreshCw className="h-3 w-3" aria-hidden />
        </button>
      )}
      {forced && status !== "outdated" && (
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Close"
          className="rounded-full p-1 hover:bg-foreground/10"
        >
          <X className="h-3 w-3" aria-hidden />
        </button>
      )}
      {checkedAt && (
        <span className="sr-only">
          {t("version.loaded")}: {BUILD_ID} · {t("version.latest")}:{" "}
          {remote?.buildId ?? "?"}
        </span>
      )}
    </div>
  );
}

export default VersionBadge;